// Recurring Invoice — new / edit / clone a profile (spec §1).
// Left half: who, what and when. Right half: the lines billed every cycle,
// with Taxable / VAT / Net totals underneath.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { Btn, IC, StatusPill } from "./RecurringParts";
import {
  INVOICE_TYPES,
  REPEATS,
  STATUSES,
  TERMS,
  blankLine,
  cap,
  cycleWord,
  firstRunFrom,
  fmtD,
  lineOf,
  money,
  money2,
  monthlyValue,
  num,
  totalsOf,
  upcomingRuns,
  validateProfile,
} from "./recurringModel";

const UNIT = { Daily: "day", Weekly: "week", Monthly: "month", Quarterly: "quarter", Yearly: "year" };

const rsStyles = {
  menuPortal: (b) => ({ ...b, zIndex: 11000 }),
  menu: (b) => ({ ...b, zIndex: 11000 }),
};
const portal = typeof document !== "undefined" ? document.body : null;

// fields are module-level so typing never remounts an input
const Field = ({ label, req, full, sel, hint, hintBad, children }) => (
  <div className={"axrc-f" + (full ? " full" : "") + (sel ? " sel" : "")}>
    <label>
      {label}
      {req ? <span className="req">*</span> : null}
    </label>
    {children}
    {sel ? IC.caret : null}
    {hint ? <span className={"hint" + (hintBad ? " bad" : "")}>{hint}</span> : null}
  </div>
);

const NativeSelect = ({ value, onChange, options, placeholder, err, id }) => (
  <select id={id} className={err ? "err" : ""} value={value} onChange={(e) => onChange(e.target.value)}>
    {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
    {options.map((o) => (
      <option key={o.v} value={o.v}>
        {o.l}
      </option>
    ))}
  </select>
);

const ProfileForm = ({ record, mode, lookups, permit, onSave, onCancel, onNewCustomer, saving }) => {
  const [d, setD] = useState(record);
  const [errs, setErrs] = useState({});
  const [msg, setMsg] = useState("");
  const dRef = useRef(d);
  dRef.current = d;

  useEffect(() => {
    setD(record);
    setErrs({});
    setMsg("");
  }, [record]);

  const { customers = [], salespeople = [], warehouses = [], products = [], taxes = [] } = lookups;

  const custOpts = useMemo(() => customers.map((c) => ({ value: c.guid, label: c.name, c })), [customers]);
  const prodOpts = useMemo(() => products.map((p) => ({ value: p.guid, label: p.name, p })), [products]);

  const patch = useCallback((ch) => {
    setD((cur) => {
      const next = { ...cur, ...ch };
      // the next date follows the schedule once any schedule field changes
      if (["start", "end", "repeat", "every"].some((k) => k in ch)) next.next = firstRunFrom(next.start, next.end, next.repeat, next.every);
      return next;
    });
    setErrs((e) => {
      const n = { ...e };
      Object.keys(ch).forEach((k) => delete n[k]);
      return n;
    });
    setMsg("");
  }, []);

  const setLine = (i, ch) =>
    setD((cur) => {
      const items = cur.items.slice();
      items[i] = { ...items[i], ...ch };
      return { ...cur, items };
    });

  const pickProduct = (i, opt) => {
    const p = opt ? opt.p : null;
    const line = d.items[i];
    const tax = p ? taxes.find((t) => t.guid === p.taxID || t.guid === p.tax?.guid) : null;
    setLine(i, {
      productGuid: p ? p.guid : "",
      productName: p ? p.name : "",
      rate: p && !num(line.rate) ? num(p.retailRate) : line.rate,
      vatPct: tax ? num(tax.percentage) : line.vatPct,
    });
    setErrs((e) => ({ ...e, items: undefined }));
  };

  const addLine = () => setD((cur) => ({ ...cur, items: [...cur.items, blankLine()] }));
  const delLine = (i) =>
    setD((cur) => (cur.items.length === 1 ? { ...cur, items: [blankLine()] } : { ...cur, items: cur.items.filter((_, k) => k !== i) }));

  const pickCustomer = (opt) => {
    const c = opt ? opt.c : null;
    patch({
      custGuid: c ? c.guid : "",
      custName: c ? c.name : "",
      custPhone: c ? c.mobile || c.phone || "" : "",
      custEmail: c ? c.email || "" : "",
      ...(c && !dRef.current.email && c.email ? { email: c.email } : {}),
    });
  };

  const submit = useCallback(
    (status) => {
      const cur = dRef.current;
      const out = { ...cur, status: status || cur.status };
      const bad = validateProfile(out);
      if (bad.length) {
        const e = {};
        bad.forEach(([k]) => {
          e[k] = true;
        });
        setErrs(e);
        setMsg(bad[0][1]);
        return;
      }
      onSave(out);
    },
    [onSave]
  );

  // F9 saves (and activates a new profile)
  useEffect(() => {
    const k = (e) => {
      if (e.key === "F9") {
        e.preventDefault();
        submit(mode === "edit" ? undefined : "Active");
      }
    };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [submit, mode]);

  const t = totalsOf(d.items);
  const runs = upcomingRuns(d.next, d.end, d.repeat, d.every);
  const perCycle = { ...d, net: t.net };
  const cust = customers.find((c) => c.guid === d.custGuid);
  const canSave = mode === "edit" ? permit.edit : permit.add;

  return (
    <section className="axrc-screen">
      <div className="axrc-phead">
        <div>
          <h1>{mode === "edit" ? "Edit recurring invoice profile" : mode === "clone" ? "Clone recurring invoice profile" : "New recurring invoice profile"}</h1>
          <div className="sub">
            {mode === "edit" ? (d.no || "") + " · changes apply from the next cycle" : "Set it once — the scheduler raises the invoice every cycle"}
          </div>
        </div>
        <div className="sp" />
        {mode === "edit" ? (
          <StatusPill s={d.status} />
        ) : (
          <span className="axrc-pill draft">
            <span className="d" />
            Not saved
          </span>
        )}
        <Btn onClick={onCancel}>Cancel</Btn>
        {canSave && mode !== "edit" && (
          <Btn onClick={() => submit("Paused")} disabled={saving} title="Saved as Paused — the scheduler skips it until you activate it">
            Save as draft
          </Btn>
        )}
        {canSave && (
          <Btn kind="primary" icon={IC.check} onClick={() => submit(mode === "edit" ? undefined : "Active")} disabled={saving}>
            {mode === "edit" ? "Save" : "Save & activate"} <span className="kbd">F9</span>
          </Btn>
        )}
      </div>

      <div className="axrc-formsplit">
        {/* ---------------- LEFT: header ---------------- */}
        <div className="axrc-card axrc-formpane">
          <div className="axrc-chead">
            <h2>Profile header</h2>
            <span className="sp" />
            <span className="note">Who, what and when</span>
          </div>
          <div className="axrc-formscroll">
            <div className="axrc-secthead">
              <span>Document</span>
            </div>
            <div className="axrc-formgrid">
              <Field label="Date" req>
                <input type="date" className={errs.date ? "err" : ""} value={d.date} onChange={(e) => patch({ date: e.target.value })} />
              </Field>
              <Field label="Profile no.">
                <input className="mono" readOnly value={d.no || ""} placeholder="Assigned on save" />
              </Field>
              <Field label="Customer" req full hint={cust ? [cust.mobile || cust.phone, cust.email].filter(Boolean).join(" · ") : ""}>
                <div className="withbtn">
                  <div className={"axrc-rs-wrap" + (errs.custGuid ? " err" : "")}>
                    <Select
                      inputId="rcFCust"
                      classNamePrefix="axrc-rs"
                      options={custOpts}
                      value={custOpts.find((o) => o.value === d.custGuid) || (d.custGuid ? { value: d.custGuid, label: d.custName } : null)}
                      onChange={pickCustomer}
                      placeholder="Select a customer"
                      isClearable
                      menuPortalTarget={portal}
                      styles={rsStyles}
                    />
                  </div>
                  {permit.add && (
                    <Btn onClick={onNewCustomer} title="Add a customer">
                      + New
                    </Btn>
                  )}
                </div>
              </Field>
              <Field label="Salesperson" req sel>
                <NativeSelect
                  id="rcFSales"
                  err={errs.salesGuid}
                  value={d.salesGuid}
                  onChange={(v) => patch({ salesGuid: v, salesperson: (salespeople.find((s) => s.guid === v) || {}).name || "" })}
                  options={salespeople.map((s) => ({ v: s.guid, l: s.name }))}
                  placeholder="Select a salesperson"
                />
              </Field>
              <Field label="Warehouse" req sel>
                <NativeSelect
                  id="rcFWh"
                  err={errs.whGuid}
                  value={d.whGuid}
                  onChange={(v) => patch({ whGuid: v, warehouse: (warehouses.find((w) => w.guid === v) || {}).name || "" })}
                  options={warehouses.map((w) => ({ v: w.guid, l: w.name }))}
                  placeholder="Select a warehouse"
                />
              </Field>
              <Field label="Main service / product" req hint="Groups the profile on every report.">
                <div className={"axrc-rs-wrap" + (errs.servGuid ? " err" : "")}>
                  <Select
                    inputId="rcFServ"
                    classNamePrefix="axrc-rs"
                    options={prodOpts}
                    value={prodOpts.find((o) => o.value === d.servGuid) || (d.servGuid ? { value: d.servGuid, label: d.service } : null)}
                    onChange={(o) => patch({ servGuid: o ? o.value : "", service: o ? o.label : "" })}
                    placeholder="Select the main service or product"
                    isClearable
                    menuPortalTarget={portal}
                    styles={rsStyles}
                  />
                </div>
              </Field>
              <Field label="Invoice type" req sel>
                <NativeSelect
                  id="rcFType"
                  err={errs.type}
                  value={d.type}
                  onChange={(v) => patch({ type: v })}
                  options={INVOICE_TYPES.map((x) => ({ v: x, l: x }))}
                  placeholder="Select the invoice type"
                />
              </Field>
            </div>

            <div className="axrc-secthead">
              <span>Schedule</span>
            </div>
            <div className="axrc-formgrid">
              <Field label="Repeat every" req sel>
                <NativeSelect id="rcFRepeat" value={d.repeat} onChange={(v) => patch({ repeat: v })} options={REPEATS.map((x) => ({ v: x, l: x }))} />
              </Field>
              <Field label="Interval" hint={"Every " + d.every + " " + (UNIT[d.repeat] || "cycle") + (d.every > 1 ? "s" : "")}>
                <input type="number" min="1" max="12" className="mono" value={d.every} onChange={(e) => patch({ every: Math.max(1, Math.min(99, parseInt(e.target.value, 10) || 1)) })} />
              </Field>
              <Field label="Start date" req>
                <input type="date" className={errs.start ? "err" : ""} value={d.start} onChange={(e) => patch({ start: e.target.value })} />
              </Field>
              <Field label="End date">
                <input type="date" className={errs.end ? "err" : ""} value={d.end} disabled={!d.end && d.neverEnds !== false} onChange={(e) => patch({ end: e.target.value })} />
                <label className="axrc-switch small">
                  <input
                    type="checkbox"
                    checked={!d.end && d.neverEnds !== false}
                    onChange={(e) => patch(e.target.checked ? { end: "", neverEnds: true } : { neverEnds: false })}
                  />
                  <span className="track" />
                  <span>Never ends</span>
                </label>
              </Field>
              <Field label="Next invoice date" hint="Worked out from the schedule.">
                <input type="date" readOnly value={d.next} />
              </Field>
              <Field label="Payment terms" req sel>
                <NativeSelect id="rcFTerms" err={errs.terms} value={d.terms} onChange={(v) => patch({ terms: v })} options={TERMS.map((x) => ({ v: x.k, l: x.k }))} />
              </Field>
              <div className="axrc-f full">
                <div className="axrc-schedprev">
                  <div className="h">
                    {IC.cal}
                    The next five invoices
                  </div>
                  <ol>{runs.length ? runs.map((x) => <li key={x}>{fmtD(x)}</li>) : <li>Nothing scheduled</li>}</ol>
                  <div className="sum">
                    {cap(cycleWord(d))} from <b>{fmtD(d.start)}</b>
                    {d.end ? (
                      <>
                        {" "}
                        until <b>{fmtD(d.end)}</b>
                      </>
                    ) : (
                      <>
                        {" "}
                        with <b>no end date</b>
                      </>
                    )}{" "}
                    · <b>{money2(t.net)}</b> each time
                  </div>
                </div>
              </div>
            </div>

            <div className="axrc-secthead">
              <span>Delivery &amp; status</span>
            </div>
            <div className="axrc-formgrid">
              <Field label="Auto email the invoice">
                <label className="axrc-switch">
                  <input type="checkbox" checked={d.autoEmail} onChange={(e) => patch({ autoEmail: e.target.checked })} />
                  <span className="track" />
                  <span>{d.autoEmail ? "Yes — send it to the customer" : "No"}</span>
                </label>
              </Field>
              <Field
                label="Email ID"
                req={d.autoEmail}
                hint={d.autoEmail ? "The invoice PDF goes here the moment it is raised." : "Needed only when auto email is on."}
              >
                <input
                  className={errs.email ? "err" : ""}
                  placeholder="accounts@customer.com"
                  autoComplete="off"
                  disabled={!d.autoEmail}
                  value={d.email}
                  onChange={(e) => patch({ email: e.target.value })}
                />
              </Field>
              <Field label="Status" req sel>
                <NativeSelect id="rcFStatus" value={d.status} onChange={(v) => patch({ status: v })} options={STATUSES.map((x) => ({ v: x, l: x }))} />
              </Field>
              <div className="axrc-f">
                <label>&nbsp;</label>
                <div className="axrc-statusnote">
                  {d.status === "Active" ? (
                    <>
                      Only an <b>Active</b> profile is picked up by the scheduler.
                    </>
                  ) : d.status === "Paused" ? (
                    "Paused — nothing is raised until you set it back to Active."
                  ) : d.status === "Completed" ? (
                    "Completed — the contract has run its course."
                  ) : (
                    "Cancelled — the profile stays for the record but never bills again."
                  )}
                </div>
              </div>
              <Field label="Notes" full>
                <textarea placeholder="Prints on every invoice raised from this profile" value={d.notes} onChange={(e) => patch({ notes: e.target.value })} />
              </Field>
            </div>
          </div>
        </div>

        {/* ---------------- RIGHT: items ---------------- */}
        <div className="axrc-card axrc-formpane">
          <div className="axrc-chead">
            <h2>Items on every invoice</h2>
            <span className="sp" />
            <span className="note">
              {d.items.length} line{d.items.length === 1 ? "" : "s"} · repeated every cycle
            </span>
          </div>
          <div className="axrc-formscroll">
            <div className="axrc-itemwrap">
              <table className={"axrc-gtable" + (errs.items ? " err" : "")}>
                <thead>
                  <tr>
                    <th style={{ width: 190 }}>Item</th>
                    <th style={{ width: 104 }}>Note</th>
                    <th className="num" style={{ width: 56 }}>
                      Qty
                    </th>
                    <th className="num" style={{ width: 80 }}>
                      Rate
                    </th>
                    <th className="num" style={{ width: 60 }}>
                      Disc %
                    </th>
                    <th className="num" style={{ width: 80 }}>
                      Disc. rate
                    </th>
                    <th style={{ width: 54, textAlign: "center" }}>Taxable</th>
                    <th className="num" style={{ width: 56 }}>
                      VAT %
                    </th>
                    <th className="num" style={{ width: 72 }}>
                      VAT
                    </th>
                    <th className="num" style={{ width: 88 }}>
                      Net amount
                    </th>
                    <th style={{ width: 28 }} />
                  </tr>
                </thead>
                <tbody>
                  {d.items.map((l, i) => {
                    const x = lineOf(l);
                    return (
                      <tr key={l.key}>
                        <td>
                          <Select
                            classNamePrefix="axrc-rs"
                            className="axrc-rs-line"
                            options={prodOpts}
                            value={prodOpts.find((o) => o.value === l.productGuid) || (l.productGuid ? { value: l.productGuid, label: l.productName } : null)}
                            onChange={(o) => pickProduct(i, o)}
                            placeholder="Select an item"
                            menuPortalTarget={portal}
                            styles={rsStyles}
                            aria-label={"Item on line " + (i + 1)}
                          />
                        </td>
                        <td>
                          <input value={l.note} placeholder="Optional" onChange={(e) => setLine(i, { note: e.target.value })} />
                        </td>
                        <td>
                          <input className="num" type="number" min="0" step="1" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} />
                        </td>
                        <td>
                          <input className="num" type="number" min="0" step="0.01" value={l.rate} onChange={(e) => setLine(i, { rate: e.target.value })} />
                        </td>
                        <td>
                          <input className="num" type="number" min="0" max="100" step="0.5" value={l.disc} onChange={(e) => setLine(i, { disc: e.target.value })} />
                        </td>
                        <td className="calc num">{money2(x.discRate)}</td>
                        <td style={{ textAlign: "center" }}>
                          <input type="checkbox" className="chk" checked={l.taxable} onChange={(e) => setLine(i, { taxable: e.target.checked })} aria-label="Taxable" />
                        </td>
                        <td>
                          <input
                            className="num"
                            type="number"
                            min="0"
                            max="100"
                            step="1"
                            disabled={!l.taxable}
                            value={l.vatPct}
                            onChange={(e) => setLine(i, { vatPct: e.target.value })}
                          />
                        </td>
                        <td className="calc num">{money2(x.vat)}</td>
                        <td className="calc num strong">{money2(x.net)}</td>
                        <td>
                          <button type="button" className="axrc-rowdel" aria-label="Remove line" onClick={() => delLine(i)}>
                            {IC.del}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button type="button" className="axrc-addrow" onClick={addLine}>
              {IC.plus}
              Add item
            </button>
          </div>
          <div className="axrc-totbar">
            <div className="t">
              <div className="l">Taxable total</div>
              <div className="v">{money2(t.taxable)}</div>
            </div>
            <div className="t">
              <div className="l">VAT total</div>
              <div className="v">{money2(t.vat)}</div>
            </div>
            <div className="t net">
              <div className="l">Net total</div>
              <div className="v">{money2(t.net)}</div>
            </div>
          </div>
          <div className="axrc-formfoot">
            <span className="msg">{msg}</span>
            <span className="sp" />
            <span className="axrc-dim">
              {cap(cycleWord(d))} · {money2(t.net)} per cycle · {money(monthlyValue(perCycle))} per month
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProfileForm;
